import { createServerFn } from '@tanstack/react-start'
import type { JobState } from 'bullmq'
import { z } from 'zod'

import { getActiveCurrentSessionUser } from '@/server/Auth/currentSession.ts'
import { createWebsiteExtensionUpdateQueue } from '@/server/UpdateWorker/updateScheduler.service.ts'

const updateTransferStatusSchema = z.object({
    ids: z.array(z.string().uuid()).min(1).max(50),
})

export type UpdateTransferQueueStatus = {
    id: string
    status: 'queued' | 'running' | 'completed' | 'failed'
    message: string
}

function getUpdateTransferQueueStatus(
    id: string,
    state: JobState | 'unknown',
    failedReason?: string,
): UpdateTransferQueueStatus | null {
    if (state === 'completed') {
        return {
            id,
            status: 'completed',
            message: 'Update wurde erfolgreich abgeschlossen.',
        }
    }

    if (state === 'failed') {
        return {
            id,
            status: 'failed',
            message: failedReason || 'Update ist fehlgeschlagen.',
        }
    }

    if (state === 'active') {
        return {
            id,
            status: 'running',
            message: 'Update wird ausgeführt.',
        }
    }

    if (
        state === 'waiting' ||
        state === 'delayed' ||
        state === 'prioritized' ||
        state === 'waiting-children'
    ) {
        return {
            id,
            status: 'queued',
            message: 'Update wartet auf die Ausführung.',
        }
    }

    return null
}

export const getUpdateTransferStatusesHandler = createServerFn({
    method: 'POST',
})
    .validator((data: unknown) => updateTransferStatusSchema.parse(data))
    .handler(async function ({ data }) {
        const sessionUser = await getActiveCurrentSessionUser()

        if (!sessionUser) {
            throw new Error('Du musst angemeldet sein.')
        }

        const queue = createWebsiteExtensionUpdateQueue()

        try {
            const jobs = await Promise.all(
                data.ids.map((id) => queue.getJob(id)),
            )
            const statuses = await Promise.all(
                jobs.map(async function (job, index) {
                    if (!job || job.data.requestedByUserId !== sessionUser.id) {
                        return null
                    }

                    return getUpdateTransferQueueStatus(
                        data.ids[index]!,
                        await job.getState(),
                        job.failedReason,
                    )
                }),
            )

            return statuses.filter(
                (status): status is UpdateTransferQueueStatus =>
                    status !== null,
            )
        } finally {
            await queue.close()
        }
    })
